import apiClient from "@apis/index";
import Routes from "@apis/routes";
import { setCompanyLocations, setUserInfo } from "@redux/slices/userInfoSlice";
import { userUpdated } from "@src/types/user";
import Constants from "expo-constants";

export const getPrefixBeforeVersion = (segment: string) => {
  const versionRegex = /\d+_\d+(_\d+)?/;
  const parts = segment.split(" ");
  const versionIndex = parts.findIndex((p) => versionRegex.test(p));
  const filtered = versionIndex !== -1 ? parts.slice(0, versionIndex) : parts;
  return filtered.join(" ");
};

export const parseUserAgent = (ua: string) => {
  const browserMatch = ua.match(
    /(Chrome|Firefox|Safari|Edge|Opera|Version)[/\s](\d+\.\d+(\.\d+)?)/
  );
  const osMatch = ua.match(/\(([^)]+)\)/);

  let deviceType = "Unknown";
  let architecture = "Unknown";
  let platformType = "Unknown";

  if (osMatch && osMatch[1]) {
    const osParts = osMatch[1].split("; ");
    deviceType = osParts[0] || "Unknown";

    if (osParts[1]) {
      architecture = getPrefixBeforeVersion(osParts[1]);
    }
  }

  if (/Mobile|iPhone|Android/i.test(ua)) {
    platformType = "Mobile Browser";
  } else if (/Electron|ReactNative|Expo/i.test(ua)) {
    platformType = "App Container";
  } else {
    platformType = "Web Browser";
  }

  return {
    browser:
      browserMatch?.[1] === "Version"
        ? "Safari"
        : browserMatch?.[1] || "Unknown",
    deviceType,
    architecture,
    platformType,
  };
};

export const formatDate = (date: string | Date) => {
  const customDate = new Date(date);

  const options = { hour: "2-digit", minute: "2-digit", hour12: true };
  const time = customDate.toLocaleTimeString("en-US", options);

  const day = customDate.getDate();
  const daySuffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[customDate.getMonth()];
  const year = customDate.getFullYear();

  return `${time} ${day}${daySuffix} ${month}, ${year}`;
};

export const getLastLoginDetailsTimeDifference = (
  userLastLoginDetails: any
) => {
  if (userLastLoginDetails && userLastLoginDetails?.timestamp) {
    const storedDate = new Date(userLastLoginDetails?.timestamp);
    const now = new Date();

    const diffMs = now.getTime() - storedDate.getTime();
    return diffMs / (1000 * 60 * 60 * 24);
  } else return 10000;
};

export const isMfaExpired = (userLastLoginDetails: any) => {
  const diffDays = getLastLoginDetailsTimeDifference(userLastLoginDetails);

  if (!diffDays || isNaN(diffDays)) {
    return true;
  }
  return diffDays >= 30;
};

export const getUserDetails = async (
  userId: number,
  dispatch: any,
  companyLocations: any,
  token: string
) => {
  const { data } = await apiClient.get<userUpdated>(
    Routes.GET_USER_DETAILS(userId),
    {
      params: {
        config_version: process.env.EXPO_PUBLIC_APP_CONFIG_VERSION
          ? process.env.EXPO_PUBLIC_APP_CONFIG_VERSION
          : Constants?.expoConfig?.extra?.appConfigVersion,
      },
    }
  );

  dispatch(setUserInfo({ ...data.user, token: token }));
  if (companyLocations.length === 0 && data.user.company_locations.length > 0) {
    dispatch(setCompanyLocations(data.user.company_locations));
  }

  return data;
};
